import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { BulkUpdateRuleApplicationInput } from "@common/schemas/transaction.schema";
import { RuleSource } from "@common/types/transactionRule";
import { useScope } from "@state/scopeContext.lib";
import {
    invalidateRuleApplicationQueries,
    useCreateRule,
    useUpdateRule,
    useWorkspaceRuleGroupsList,
} from "@api/rules.api";
import { useEntities } from "@hooks/useEntities";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    BulkUpdateDetails,
    BulkUpdateMode,
    BulkUpdateParams,
    BulkUpdateStatus,
} from "../../../transactions/TransactionsBulkActions/lib";
import { TOAST_MESSAGE_KEY } from "../../../transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider.lib";
import { BulkUpdateFailureError } from "../../../transactions/TransactionsBulkActions/bulkUpdateFailure";
import { PaginatedTransactionsActionsContext } from "../../../transactions/PaginatedTransactionsTable.context";
import { SimilarTransactionsUpdateResultToast } from "../../SimilarTransactionsUpdateToast/SimilarTransactionsUpdateToast";
import {
    conditionFormRowToModel,
    formActionsToModel,
} from "../../../settings/Rules/TransactionRuleForm/formToModel.helpers";
import {
    FormRuleActionType,
    mapRuleActionTypeToForm,
} from "../../../settings/Rules/TransactionRuleForm/constants";
import { buildActionsPayloads } from "./buildActionsPayloads";
import { useActionsContext } from "./ActionsContext";
import { ActionRowDefinition, ActionRowRuleMatch } from "./types";
import {
    ActionRowRuleMatchMap,
    classifyActionRowRuleMatches,
} from "./ruleMatchClassification";
import { getUpdatedValueSummary } from "./updatedValueSummary";
import {
    buildRuleSaveFailureMessage,
    getRuleSaveErrorMessage,
    UNKNOWN_RULE_SAVE_ERROR,
} from "./ruleSaveError";

interface Params {
    filters: TransactionFiltersDto;
    displayedChangedEntityName?: string;
    totalTransactions: number;
    onDone: () => void;
}

/**
 * Per-row decision collected from the conflict modal. Only rows classified
 * as `conflict_same_type_diff_value` are present here.
 */
export type ConflictDecision = "override" | "skip";

export type ConflictDecisions = Record<string, ConflictDecision>;

interface ConflictModalState {
    isOpen: boolean;
    conflicts: ActionRowDefinition[];
    ruleMatches: ActionRowRuleMatchMap;
}

interface PersistRuleForRowParams {
    row: ActionRowDefinition;
    order: number;
    decisions: ConflictDecisions;
    ruleMatches: ActionRowRuleMatchMap;
}

interface PersistedRuleForRow {
    rowId: string;
    ruleId: string;
    actionTypes: Array<BulkUpdateRuleApplicationInput["actionType"]>;
}

/**
 * Drop the existing rule's action that shares this row's form action type, so
 * the row's new action replaces it while every other action is preserved.
 */
function omitSameTypeActions(
    actions: ActionRowRuleMatch["existingActions"],
    formActionType: FormRuleActionType,
): ActionRowRuleMatch["existingActions"] {
    return actions.filter((action) => {
        try {
            return (
                mapRuleActionTypeToForm(action.actionType) !== formActionType
            );
        } catch {
            return true;
        }
    });
}

function buildRuleApplicationsByRowId(
    persistedRules: PersistedRuleForRow[],
): Map<string, BulkUpdateRuleApplicationInput[]> {
    return new Map(
        persistedRules.map((persistedRule) => [
            persistedRule.rowId,
            persistedRule.actionTypes.map((actionType) => ({
                ruleId: persistedRule.ruleId,
                actionType,
            })),
        ]),
    );
}

export function buildBulkDetails(
    valueToAssign: BulkUpdateParams,
    displayedChangedEntityName: string | undefined,
    transactionsCount: number,
): BulkUpdateDetails {
    const common = {
        status: BulkUpdateStatus.UPDATING,
        transactionsCount,
        mode: BulkUpdateMode.FILTERS,
    };

    /**
     * TypeScript narrows the discriminated union by switching on `update`,
     * which lets us attach `counterpartyName` / `accountName` only to the
     * variants that allow them.
     */
    if (
        valueToAssign.update === "counterpartyId" &&
        displayedChangedEntityName
    ) {
        return {
            ...common,
            ...valueToAssign,
            counterpartyName: displayedChangedEntityName,
        };
    }
    if (
        valueToAssign.update === "coa" ||
        valueToAssign.update === "accrualCoa"
    ) {
        return {
            ...common,
            ...valueToAssign,
            accountName:
                displayedChangedEntityName ?? valueToAssign.accountName,
        };
    }
    return { ...common, ...valueToAssign };
}

export function useConfirmActions({
    filters,
    displayedChangedEntityName,
    totalTransactions,
    onDone,
}: Params) {
    const { workspaceId } = useScope();
    const {
        rows,
        ruleGroupId,
        valueToAssign,
        rowIdToOwnedTxIds,
        scopeConditions,
    } = useActionsContext();
    const { doUpdate } = useContext(PaginatedTransactionsActionsContext);
    const { toast, error } = useToaster();
    const entities = useEntities({ excludeMockEntities: true });
    const ruleGroupsList = useWorkspaceRuleGroupsList();
    const createRule = useCreateRule();
    const updateRule = useUpdateRule();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const savedRulesByRowIdRef = useRef(new Map<string, PersistedRuleForRow>());
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [conflictModalState, setConflictModalState] =
        useState<ConflictModalState>({
            isOpen: false,
            conflicts: [],
            ruleMatches: {},
        });

    const selectedGroup = useMemo(
        () => ruleGroupsList.data?.find((g) => g.id === ruleGroupId),
        [ruleGroupsList.data, ruleGroupId],
    );

    const buildRuleMatchMap = useCallback(
        (selectedRuleGroup = selectedGroup): ActionRowRuleMatchMap =>
            classifyActionRowRuleMatches({
                rows,
                selectedGroup: selectedRuleGroup,
                scopeConditions,
            }),
        [rows, selectedGroup, scopeConditions],
    );

    const withRuleMatches = useCallback(
        (ruleMatches: ActionRowRuleMatchMap) =>
            rows.map((row) => ({
                ...row,
                ruleMatch: ruleMatches[row.id] ?? undefined,
            })),
        [rows],
    );

    const persistRuleForRow = useCallback(
        async ({
            row,
            order,
            decisions,
            ruleMatches,
        }: PersistRuleForRowParams): Promise<PersistedRuleForRow | null> => {
            if (!workspaceId) {
                return null;
            }
            if (row.conditions.length === 0) {
                return null;
            }

            const conditions = [...scopeConditions, ...row.conditions]
                .map(conditionFormRowToModel)
                .filter((c): c is NonNullable<typeof c> => !!c);
            if (conditions.length === 0) {
                return null;
            }
            const newActions = formActionsToModel([row.action]);
            if (newActions.length === 0) {
                return null;
            }
            const actionTypes = [
                ...new Set(newActions.map((action) => action.actionType)),
            ];

            const match = ruleMatches[row.id];

            if (match?.kind === "skip_same_value") {
                return null;
            }

            // Honour a "skip" decision before the source guard, so an AI
            // conflict the user declined is never POSTed as a custom rule.
            if (
                match?.kind === "conflict_same_type_diff_value" &&
                decisions[row.id] !== "override"
            ) {
                return null;
            }

            if (match?.existingRuleSource === RuleSource.NORMALIZATION) {
                /**
                 * AI (NORMALIZATION) rules cannot be PATCHed. Override or merge
                 * onto one by POSTing a custom rule at `order: 0` — the only
                 * value that can tie the pinned AI rule — carrying the AI rule's
                 * other-type actions so its category/counterparty keeps applying
                 * once the custom rule wins the group's first match (the backend
                 * breaks the equal-order tie custom-first). For a `merge` match
                 * the omit is a no-op.
                 */
                const createdRule = await createRule.mutateAsync({
                    workspaceId,
                    rule: {
                        groupId: ruleGroupId,
                        actions: [
                            ...omitSameTypeActions(
                                match.existingActions,
                                row.actionType,
                            ),
                            ...newActions,
                        ],
                        conditions,
                        order: 0,
                    },
                    source: RuleSource.TRANSACTIONS_FEED,
                    applyToExistingTransactions: false,
                    suppressErrorToast: true,
                });
                return {
                    rowId: row.id,
                    ruleId: createdRule.id,
                    actionTypes,
                };
            }

            if (match?.kind === "conflict_same_type_diff_value") {
                const decision = decisions[row.id];
                if (decision !== "override") {
                    return null;
                }
                // Override path: replace the same-type action on the existing
                // rule with the row's new action, keeping every other action.
                await updateRule.mutateAsync({
                    workspaceId,
                    ruleId: match.existingRuleId,
                    rule: {
                        groupId: ruleGroupId,
                        actions: [
                            ...omitSameTypeActions(
                                match.existingActions,
                                row.actionType,
                            ),
                            ...newActions,
                        ],
                        conditions,
                    },
                    applyToExistingTransactions: false,
                    suppressErrorToast: true,
                });
                return {
                    rowId: row.id,
                    ruleId: match.existingRuleId,
                    actionTypes,
                };
            }

            if (match?.kind === "merge") {
                /**
                 * Merge path: the backend replaces `actions` wholesale, so we
                 * combine the existing rule's actions with ours.
                 */
                await updateRule.mutateAsync({
                    workspaceId,
                    ruleId: match.existingRuleId,
                    rule: {
                        groupId: ruleGroupId,
                        actions: [...match.existingActions, ...newActions],
                        conditions,
                    },
                    applyToExistingTransactions: false,
                    suppressErrorToast: true,
                });
                return {
                    rowId: row.id,
                    ruleId: match.existingRuleId,
                    actionTypes,
                };
            }

            const createdRule = await createRule.mutateAsync({
                workspaceId,
                rule: {
                    groupId: ruleGroupId,
                    actions: newActions,
                    conditions,
                    order,
                },
                source: RuleSource.TRANSACTIONS_FEED,
                applyToExistingTransactions: false,
                suppressErrorToast: true,
            });
            return {
                rowId: row.id,
                ruleId: createdRule.id,
                actionTypes,
            };
        },
        [workspaceId, ruleGroupId, scopeConditions, createRule, updateRule],
    );

    /**
     * Shared tail: persist rules according to the click-time `ruleMatches`
     * snapshot and (for conflict rows) user decisions, then close the overlay
     * and run the bulk tx update. A rule failure keeps the overlay open and
     * skips the bulk update so the user can fix the rule and retry.
     */
    const execute = useCallback(
        async (
            decisions: ConflictDecisions,
            ruleMatches: ActionRowRuleMatchMap,
        ) => {
            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);

            const bulkDetails = buildBulkDetails(
                valueToAssign,
                displayedChangedEntityName,
                totalTransactions,
            );
            const updatedValueSummary = getUpdatedValueSummary({
                rows,
                rowIdToOwnedTxIds,
                valueToAssign,
                entities,
            });

            /**
             * Create/merge/override rules in table order so drag-and-drop
             * order corresponds to rule insertion order for newly-created
             * rules.
             */
            const rowsToPersist = rows.filter(
                (r) => r.createRule && r.conditions.length > 0,
            );
            const ruleSaveFailures: string[] = [];
            const persistedRules: PersistedRuleForRow[] = [];
            for (let i = 0; i < rowsToPersist.length; i++) {
                try {
                    const persistedRule = await persistRuleForRow({
                        row: rowsToPersist[i],
                        order: i,
                        decisions,
                        ruleMatches,
                    });
                    const savedRule =
                        persistedRule ??
                        (ruleMatches[rowsToPersist[i].id]?.kind ===
                        "skip_same_value"
                            ? savedRulesByRowIdRef.current.get(
                                  rowsToPersist[i].id,
                              )
                            : undefined);
                    if (savedRule) {
                        persistedRules.push(savedRule);
                        savedRulesByRowIdRef.current.set(
                            savedRule.rowId,
                            savedRule,
                        );
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to persist rule for row", err);
                    ruleSaveFailures.push(
                        err instanceof Error
                            ? getRuleSaveErrorMessage(err)
                            : UNKNOWN_RULE_SAVE_ERROR,
                    );
                }
            }
            const persistedRuleIds = [
                ...new Set(persistedRules.map((rule) => rule.ruleId)),
            ];

            if (ruleSaveFailures.length > 0) {
                if (persistedRuleIds.length > 0 && workspaceId) {
                    await invalidateRuleApplicationQueries(workspaceId);
                }
                error(buildRuleSaveFailureMessage(ruleSaveFailures));
                setIsSubmitting(false);
                return;
            }

            savedRulesByRowIdRef.current.clear();
            if (isMountedRef.current) {
                onDone();
            }

            const payloads = buildActionsPayloads({
                rows,
                filters,
                valueToAssign,
                totalTransactions,
                rowIdToOwnedTxIds,
                ruleApplicationsByRowId:
                    buildRuleApplicationsByRowId(persistedRules),
            });

            try {
                await doUpdate(bulkDetails, payloads);
            } catch (updateError) {
                if (persistedRuleIds.length > 0 && workspaceId) {
                    await invalidateRuleApplicationQueries(workspaceId);
                }
                toast(
                    <SimilarTransactionsUpdateResultToast
                        totalTransactions={totalTransactions}
                        updatedValueCount={0}
                        ruleCount={persistedRuleIds.length}
                        persistedRuleIds={persistedRuleIds}
                        valueToAssign={valueToAssign}
                        singleValueToAssign={
                            updatedValueSummary.singleValueToAssign
                        }
                        updateFailure={
                            updateError instanceof BulkUpdateFailureError
                                ? updateError
                                : { succeededCount: 0, failedCount: null }
                        }
                    />,
                    TOAST_MESSAGE_KEY,
                );
                setIsSubmitting(false);
                return;
            }

            if (workspaceId && persistedRuleIds.length > 0) {
                await invalidateRuleApplicationQueries(workspaceId);
            }

            toast(
                <SimilarTransactionsUpdateResultToast
                    totalTransactions={totalTransactions}
                    updatedValueCount={updatedValueSummary.updatedValueCount}
                    ruleCount={persistedRuleIds.length}
                    persistedRuleIds={persistedRuleIds}
                    valueToAssign={valueToAssign}
                    singleValueToAssign={
                        updatedValueSummary.singleValueToAssign
                    }
                />,
                TOAST_MESSAGE_KEY,
            );

            setIsSubmitting(false);
        },
        [
            isSubmitting,
            rows,
            filters,
            valueToAssign,
            displayedChangedEntityName,
            totalTransactions,
            onDone,
            doUpdate,
            toast,
            error,
            persistRuleForRow,
            rowIdToOwnedTxIds,
            entities,
            workspaceId,
        ],
    );

    const confirm = useCallback(async () => {
        if (isSubmitting) {
            return;
        }

        const latestRuleGroups = await ruleGroupsList.refetch();
        const latestSelectedGroup =
            latestRuleGroups.data?.find((g) => g.id === ruleGroupId) ??
            selectedGroup;
        const ruleMatches = buildRuleMatchMap(latestSelectedGroup);
        const rowsWithMatches = withRuleMatches(ruleMatches);
        const conflicts = rowsWithMatches.filter(
            (r) =>
                r.createRule &&
                r.ruleMatch?.kind === "conflict_same_type_diff_value",
        );
        if (conflicts.length === 0) {
            await execute({}, ruleMatches);
            return;
        }
        // Defer execution until the user resolves conflicts in the modal.
        setConflictModalState({ isOpen: true, conflicts, ruleMatches });
    }, [
        isSubmitting,
        ruleGroupsList,
        ruleGroupId,
        selectedGroup,
        buildRuleMatchMap,
        withRuleMatches,
        execute,
    ]);

    const resolveConflicts = useCallback(
        async (decisions: ConflictDecisions) => {
            const { ruleMatches } = conflictModalState;
            setConflictModalState((prev) => ({ ...prev, isOpen: false }));
            await execute(decisions, ruleMatches);
        },
        [conflictModalState, execute],
    );

    const cancelConflicts = useCallback(() => {
        /**
         * Back = abort everything. No tx update, no rule changes. The drawer
         * stays open so the user can revise their selection / actions.
         */
        setConflictModalState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const clearClosedConflicts = useCallback(() => {
        setConflictModalState((prev) =>
            prev.isOpen
                ? prev
                : { isOpen: false, conflicts: [], ruleMatches: {} },
        );
    }, []);

    return {
        confirm,
        isSubmitting,
        conflictModalState,
        resolveConflicts,
        cancelConflicts,
        clearClosedConflicts,
    };
}
